import React, { useState } from "react";
import { Phone, Mail, FileText, Clock, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export const LEAD_TYPES = [
  { value: "all", label: "All" },
  { value: "individuals", label: "People" },
  { value: "businesses", label: "Businesses" },
  { value: "bids", label: "Public bids" },
];

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const remainingLabel = (expiresAt, now) => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return { text: "Expired", urgent: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { text: `Expires in ${h}h ${m}m`, urgent: h < 3 };
};

const TIERS = [["low", "Low"], ["typical", "Standard"], ["high", "High"]];

const PriceTiers = ({ p, sel, setSel, service }) => (
  <>
    <div className="mt-3 text-[10px] uppercase tracking-widest text-[#5C564E]">{service} · market price</div>
    <div className="mt-1 grid grid-cols-3 gap-2">
      {TIERS.map(([k, label]) => (
        <button
          key={k}
          data-testid={`lead-price-${k}`}
          onClick={() => setSel(k)}
          className={`rounded-lg p-2 text-center border transition-colors ${sel === k ? "bg-[#1B3A4B] text-white border-[#A6844A]" : "bg-[#F4F0E8] text-[#1C1915] border-[#D5CFC2] hover:bg-[#EAE4D9]"}`}
        >
          <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
          <div className="font-semibold text-sm">{money(p[k])}</div>
        </button>
      ))}
    </div>
  </>
);

const LeadContacts = ({ lead }) => (
  <div className="mt-2 space-y-1 text-sm text-[#5C564E]">
    {lead.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#A6844A]" /> <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a></div>}
    {lead.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#A6844A]" /> <a href={`mailto:${lead.email}`} className="hover:underline break-all">{lead.email}</a></div>}
    {lead.source && lead.source.url && (
      <div className="flex items-center gap-2">
        <ExternalLink className="w-3.5 h-3.5 text-[#A6844A] shrink-0" />
        <a data-testid="lead-source-link" href={lead.source.url} target="_blank" rel="noreferrer" className="hover:underline break-all text-[#1B3A4B]">
          {lead.source.label || "View source"}
        </a>
      </div>
    )}
  </div>
);

export const LeadCard = ({ lead, onGenerate, onDelete, now }) => {
  const p = lead.pricing || {};
  const hasPricing = p.low != null && p.typical != null && p.high != null;
  const [sel, setSel] = useState("typical");
  const [custom, setCustom] = useState("");
  const rem = remainingLabel(lead.expires_at, now);

  const go = () => {
    const n = parseFloat(custom);
    const amt = sel === "custom" ? (isNaN(n) ? null : n) : hasPricing ? p[sel] : null;
    if (amt == null) {
      toast.error("Pick a price or type your own first.");
      return;
    }
    onGenerate(lead, amt);
  };

  return (
    <div data-testid="lead-card" className="rounded-2xl border border-[#E2DCD2] bg-white p-5 shadow-sm flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-serif text-xl text-[#1B3A4B]">{lead.name}</h3>
        <div className="flex items-center gap-1.5">
          {lead.trial && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E7D7B8] text-[#1B3A4B]">Free trial</span>}
          <button data-testid="lead-delete" onClick={() => onDelete(lead)} title="Delete this lead" className="text-[#A0998C] hover:text-[#B4432F] transition-colors p-1 rounded-md hover:bg-[#F4EEE6]">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {rem && (
        <div data-testid="lead-expiry" className={`mt-1 inline-flex items-center gap-1 text-xs ${rem.urgent ? "text-[#B4432F] font-semibold" : "text-[#5C564E]"}`}>
          <Clock className="w-3.5 h-3.5" /> {rem.text}
        </div>
      )}
      <LeadContacts lead={lead} />
      <p className="mt-3 text-sm text-[#1C1915] flex-1">{lead.project}</p>
      {hasPricing ? (
        <PriceTiers p={p} sel={sel} setSel={setSel} service={lead.service} />
      ) : (
        <p className="mt-3 text-xs text-[#5C564E]">No market price found — type your own below.</p>
      )}
      <Input
        data-testid="lead-custom-price"
        type="number"
        placeholder="Or type your own price"
        value={custom}
        onChange={(e) => { setCustom(e.target.value); setSel("custom"); }}
        className={`mt-2 h-9 ${sel === "custom" ? "border-[#A6844A] ring-1 ring-[#A6844A]" : ""}`}
      />
      <Button data-testid="lead-generate-quote" onClick={go} className="mt-3 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">
        <FileText className="w-4 h-4 mr-2" /> Generate Quote
      </Button>
    </div>
  );
};
